import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import { messages } from '@nesc/shared';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-overlay',
      'data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * ダイアログ（角丸 12px、影あり）。
 * スマホでは下から出るシートとして表示する。
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { sheetOnMobile?: boolean }
>(({ className, children, sheetOnMobile = true, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-50 flex flex-col bg-surface-raised shadow-dialog',
        sheetOnMobile
          ? cn(
              'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-dialog',
              'data-[state=open]:animate-slide-up data-[state=closed]:animate-slide-down',
              'safe-bottom',
              'lg:inset-auto lg:left-1/2 lg:top-1/2 lg:max-h-[85dvh] lg:w-full lg:max-w-lg',
              'lg:-translate-x-1/2 lg:-translate-y-1/2 lg:rounded-dialog',
              'lg:data-[state=open]:animate-zoom-in lg:data-[state=closed]:animate-fade-out',
            )
          : cn(
              'left-1/2 top-1/2 max-h-[85dvh] w-[calc(100%-2rem)] max-w-lg',
              '-translate-x-1/2 -translate-y-1/2 rounded-dialog',
              'data-[state=open]:animate-zoom-in data-[state=closed]:animate-fade-out',
            ),
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close
        className="absolute right-3 top-3 rounded-control p-2 text-foreground-muted transition-colors duration-hover hover:bg-muted"
        aria-label={messages.common.close}
      >
        <X aria-hidden className="size-5" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = 'DialogContent';

export const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('space-y-1.5 px-5 pb-3 pt-5', className)} {...props} />
);

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn('text-h2 pr-8', className)} {...props} />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-body text-foreground-muted', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex-1 overflow-y-auto px-5 py-2', className)} {...props} />
);

export const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse gap-2 border-t border-border px-5 py-4',
      'lg:flex-row lg:justify-end',
      className,
    )}
    {...props}
  />
);
